import type { Vec3 } from '../hs1/vec'
import { AXIS_KEYS, worldPoints, type AxisKey, type Rig, type RigNode } from './rig'

/**
 * Das Strichmännchen — der Achsbaum als vier Ansichten.
 *
 * WAS ES IST UND WAS NICHT. Es ist keine Illustration der Konstruktion, sondern
 * die Bedienoberfläche des Baums: jeder Kreis ist ein Knickpunkt, jeder Strich
 * ein Glied, jeder Kreissektor eine Winkelfreiheit. Wer einen Punkt anklickt,
 * wählt einen DOF. Ein Bild, das nur zeigt, wäre eine zweite Beschreibung neben
 * dem Baum — dieses hier IST der Baum.
 *
 * WARUM VIER ANSICHTEN UND NICHT EINE PERSPEKTIVE. Eine perspektivische Ansicht
 * ist schön und zum Ausmessen unbrauchbar: sie verkürzt, und wer eine
 * Hüftbreite abschätzen will, bekommt eine Funktion des Blickwinkels. Drei
 * senkrechte Projektionen zeigen jede Koordinate unverzerrt, und die vierte —
 * isometrisch — sagt, welches Bild zu welcher Seite gehört. Genau so zeichnet
 * eine Konstruktionsabteilung, und aus demselben Grund.
 *
 * DIESE DATEI RECHNET, SIE ZEICHNET NICHT. Sie gibt Punkte, Strecken und Bögen
 * in EINHEITSKOORDINATEN zurück; ob daraus SVG, Canvas oder ein Ausdruck wird,
 * entscheidet die Oberfläche. Deshalb steht sie im Rechenkern und hat Tests:
 * dass ein Punkt in der Seitenansicht die x-Koordinate trägt und nicht die y,
 * ist eine Aussage über die Maschine und nicht über die Darstellung — und man
 * sieht es einem Bild nicht an, in dem beide fast gleich aussehen.
 */

/** Welche Ansicht. */
export type ViewKey = 'seite' | 'vorn' | 'oben' | 'iso'

export interface ViewSpec {
  key: ViewKey
  label: string
  /** Wofür die waagerechte Bildachse steht. */
  hint: string
  /** Projektion eines Raumpunktes auf die Bildebene. */
  project: (p: Vec3) => { u: number; v: number }
  /**
   * Um welche Raumachse in dieser Ansicht gedreht wird.
   *
   * Ein Kreissektor für eine Winkelfreiheit ist nur dort ehrlich, wo die
   * Drehachse SENKRECHT auf der Bildebene steht — sonst zeichnet man eine
   * Ellipse als Kreis. In der Seitenansicht ist das das Nicken, von vorn das
   * Rollen, von oben das Gieren. Die isometrische Ansicht hat keine solche
   * Achse und bekommt deshalb keine Sektoren.
   */
  axis: AxisKey | null
}

/* Isometrie: dreissig Grad, wie in der Vorlage. */
const S30 = Math.sin(Math.PI / 6)
const C30 = Math.cos(Math.PI / 6)

/**
 * Die vier Ansichten.
 *
 * KOORDINATEN DES PUNKTSATZES: x nach vorn, y nach aussen, z nach oben. Die
 * Bildachse `v` zeigt nach oben; die Oberfläche dreht sie beim Zeichnen um, weil
 * ein Bildschirm nach unten zählt. Diese Umkehr hier vorwegzunehmen wäre eine
 * Darstellungsentscheidung mitten in einer Projektion.
 */
export const VIEWS: readonly ViewSpec[] = [
  {
    key: 'seite',
    label: 'Seite',
    hint: '+x vorn · +z oben',
    project: (p) => ({ u: p.x, v: p.z }),
    axis: 'y',
  },
  {
    key: 'vorn',
    label: 'Vorn',
    hint: '+y aussen · +z oben',
    project: (p) => ({ u: p.y, v: p.z }),
    axis: 'x',
  },
  {
    key: 'oben',
    label: 'Oben',
    hint: '+y aussen · +x vorn',
    project: (p) => ({ u: p.y, v: p.x }),
    axis: 'z',
  },
  {
    key: 'iso',
    label: 'ISO',
    hint: '',
    project: (p) => ({ u: (p.y - p.x) * C30, v: p.z + (p.y + p.x) * S30 }),
    axis: null,
  },
] as const

/** Ein gezeichneter Knickpunkt. */
export interface FigureDot {
  id: string
  label: string
  /** Welche Körperseite: 1 = rechts, −1 = links. Mittige Knoten sind 1. */
  side: 1 | -1
  /** Lage in der Bildebene, mm. */
  u: number
  v: number
  kind: RigNode['kind']
  /** Hat er eine Freiheit, die arbeitet? */
  live: boolean
  /** Ausgeschaltet? */
  off: boolean
  /** Eine Freiheit ohne Motor — passiv, etwa federgeführt. */
  passive: boolean
}

/** Ein gezeichnetes Glied. */
export interface FigureLink {
  from: string
  to: string
  side: 1 | -1
  u1: number
  v1: number
  u2: number
  v2: number
}

/** Ein Kreissektor für eine Winkelfreiheit. */
export interface FigureArc {
  id: string
  u: number
  v: number
  /** Grenzen in Grad, gegen die Grundstellung. */
  lo: number
  hi: number
}

export interface FigureView {
  view: ViewSpec
  dots: FigureDot[]
  links: FigureLink[]
  arcs: FigureArc[]
  /** Umschliessendes Rechteck aller Punkte, mm. */
  bounds: { u0: number; u1: number; v0: number; v1: number }
}

export interface FigureOptions {
  /** Die linke Körperseite mitzeichnen. */
  bothSides?: boolean
  /** Kreissektoren für die Winkelfreiheiten. */
  freedoms?: boolean
  /**
   * Welche Knoten passiv sind — Freiheit ohne Motor.
   *
   * Sie kommen von aussen und nicht aus dem Baum: welche Baugruppe an einem
   * Gelenk sitzt, ist Ebene 2, und der Baum ist Ebene 1. Ein Baum, der seine
   * Bauformen kennte, wäre die Stelle, an der die Beinlänge davon abhängt,
   * welchen Motor jemand gewählt hat.
   */
  passive?: ReadonlySet<string>
}

const EMPTY_SET: ReadonlySet<string> = new Set()

/**
 * Spiegelt einen Punkt auf die linke Körperseite.
 *
 * NUR y KEHRT SICH UM. Der Punktsatz führt die rechte Seite; links ist ihr
 * Spiegelbild an der Mittelebene. Wer x oder z mitspiegelt, bekommt einen
 * Roboter, der rückwärts steht.
 */
const mirror = (p: Vec3): Vec3 => ({ x: p.x, y: -p.y, z: p.z })

/**
 * Rechnet eine Ansicht des Baums.
 *
 * Rein: gleiche Eingaben, gleiches Ergebnis. Keine Zeit, kein Zufall, kein DOM.
 */
export function figureView(rig: Rig, view: ViewSpec, options: FigureOptions = {}): FigureView {
  const both = options.bothSides ?? true
  const showFreedoms = options.freedoms ?? false
  const passive = options.passive ?? EMPTY_SET

  const world = worldPoints(rig)
  /* Die linke Seite bekommt ihre EIGENEN Weltlagen. Ohne gewollte Unsymmetrie
     sind es dieselben Zahlen; mit ihr steht im Bild, was in der Rechnung steht
     — sonst zeigte das Strichmännchen eine symmetrische Maschine, die keine
     mehr ist. */
  const worldLeft = worldPoints(rig, 'L')
  const dots: FigureDot[] = []
  const links: FigureLink[] = []
  const arcs: FigureArc[] = []

  const sides: (1 | -1)[] = both ? [1, -1] : [1]

  for (const side of sides) {
    for (const id of rig.order) {
      const node = rig.byId[id]
      /* Ein mittiger Knoten wird nicht gespiegelt — sonst läge er zweimal
         übereinander und jeder Klick träfe zufällig einen der beiden. */
      if (side === -1 && !node.mirrored) continue

      const p = side === -1 ? mirror(worldLeft[id]) : world[id]
      const { u, v } = view.project(p)

      dots.push({
        id,
        label: node.label,
        side,
        u,
        v,
        kind: node.kind,
        live: AXIS_KEYS.some((a) => node.freedom[a] !== null) && node.off === null,
        off: node.off !== null,
        /* Eine Freiheit OHNE Motor. Sie zählt in der Achszahl und nicht in der
           Motorzahl, und im Bild soll man das sehen — sonst sucht man den
           Antrieb, den es nicht gibt. */
        passive: passive.has(id),
      })

      if (node.parent) {
        /* Der Elternknoten wird nur mitgespiegelt, wenn es ihn zweimal gibt.
           Die Hüftgierachse hängt am Becken, und das Becken ist mittig — der
           linke Strich geht deshalb an denselben Beckenpunkt. */
        const parent = rig.byId[node.parent]
        const base =
          side === -1 && parent.mirrored ? mirror(worldLeft[node.parent]) : world[node.parent]
        const a = view.project(base)
        links.push({ from: node.parent, to: id, side, u1: a.u, v1: a.v, u2: u, v2: v })
      }

      if (showFreedoms && side === 1 && view.axis) {
        const freedom = node.freedom[view.axis]
        if (freedom) arcs.push({ id, u, v, lo: freedom.lo, hi: freedom.hi })
      }
    }
  }

  let u0 = Infinity
  let u1 = -Infinity
  let v0 = Infinity
  let v1 = -Infinity
  for (const d of dots) {
    if (d.u < u0) u0 = d.u
    if (d.u > u1) u1 = d.u
    if (d.v < v0) v0 = d.v
    if (d.v > v1) v1 = d.v
  }
  if (!Number.isFinite(u0)) {
    u0 = 0
    u1 = 1
    v0 = 0
    v1 = 1
  }

  return { view, dots, links, arcs, bounds: { u0, u1, v0, v1 } }
}

/** Alle vier Ansichten auf einmal. */
export function figure(rig: Rig, options: FigureOptions = {}): FigureView[] {
  return VIEWS.map((view) => figureView(rig, view, options))
}

/**
 * Verteilt Beschriftungen, damit sie sich nicht überlagern.
 *
 * DAS EINZIGE STÜCK DARSTELLUNG IN DIESER DATEI, und es steht hier, weil es
 * eine Rechnung ist. Neun Punkte im Bein liegen im Bild dicht beieinander; ihre
 * Beschriftungen übereinander machen die Zeichnung unlesbar. Das Verfahren:
 * nach Höhe sortieren und jede Marke, die ihrer Vorgängerin zu nahe kommt, nach
 * unten schieben. Aus der Vorlage übernommen — dort steht es mitten im
 * Zeichencode, hier als Funktion, die man prüfen kann.
 *
 * `gap` ist der Mindestabstand in derselben Einheit wie `v`.
 */
export function spreadLabels<T extends { v: number }>(labels: T[], gap: number): T[] {
  const sorted = [...labels].sort((a, b) => a.v - b.v)
  const out: T[] = []
  let last = -Infinity
  for (const label of sorted) {
    const v = label.v < last + gap ? last + gap : label.v
    out.push({ ...label, v })
    last = v
  }
  return out
}
